import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { DropdownSelectorPoint } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorPoint/DropdownSelectorPoint'
import { SoilAverageDepthsToggle } from 'App/Map/PanelDetails/SectionTitleBars/SoilTitleChildren/SoilAverageDepthsToggle'
import { SoilSettingsTitleLink } from 'App/Map/PanelDetails/SectionTitleBars/SoilTitleChildren/SoilSettingsTitleLink'
import { SoilZoneDevicesToggle } from 'App/Map/PanelDetails/SectionTitleBars/SoilTitleChildren/SoilZoneDevicesToggle'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import type { TLngLat } from 'App/Map/types'
import type {
  StackedChartSection,
  StackedChartSectionItem,
  TIrrigationEventSeries,
} from 'components/StackedChart/types'
import moment from 'moment-timezone'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPointCategory } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getAvailableEmittersForScope } from './_utils/getAvailableEmittersForScope'
import { getEmitterDataForRows } from './_utils/getEmitterDataForRows'
import { getHasWaterEvents } from './_utils/getHasWaterEvents'
import { getIrrigationActivityScope } from './_utils/getIrrigationActivityScope'
import { makeAwcChart } from './_utils/makeAwcChart'
import { makeEmitterSeries } from './_utils/makeEmitterSeries'
import { makeRainSeries } from './_utils/makeRainSeries'
import { makeWaterEventsChart } from './_utils/makeWaterEventsChart'
import { PREDICTION_KEY } from './_utils/predictionKey'
import { checkPermission } from './soil'

const possibleValueTypes: VV.DomainTypes.Soil.TTimeseriesValueTypeKeysMerged[] = ['soilMoisture']
const pointCategory: TPointCategory = 'soilPoint'

// TODO: At the moment we are only making line series with the average value. We are not yet making area series out of the min/max values.
export const awcLineAndWaterEvents = ({
  data,
  soilStationLngLat,
  precipitationStationLngLat,
  compareSeasonsData,
  soilStationFavoriteDepths,
}: {
  data: routes.Values.Response
  precipitationStationLngLat: string | null
  soilStationLngLat: string | null
  compareSeasonsData: routes.Values.Response
  soilStationFavoriteDepths: SharedTypes.TSoilSensorDepth[]
}): StackedChartSection => {
  const commonReturnItems = {
    title: unitConverter.soilAwc().categoryTitleWithoutUnit(),
    titleLink: SoilSettingsTitleLink(),
    fieldAssetPicker: (
      <DropdownSelectorPoint pointCategory={pointCategory} valuesTimeseriesToFilterOn={possibleValueTypes} />
    ),
    id: 'stackem-soil-awc-line',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const soilMoisture = data?.points?.[String(soilStationLngLat)]?.values?.soilMoisture || []

  const firstTimestampFromPredictionSeries =
    soilMoisture.find((depth) => depth.metadata.depth === PREDICTION_KEY)?.timeseries[0]?.timestamp || 0

  const firstForecastTimestamp = firstTimestampFromPredictionSeries
    ? +new Date(firstTimestampFromPredictionSeries)
    : +new Date()

  const aggregationInterval = soilMoisture[0]?.metadata?.aggregationInterval
  const { compareSeasonsInterval, soilIrrigationActivityScope } = detailsPanelStore.getState()
  const { properties } = fieldAssetStore.getState()
  const { property } = selectedFieldAssetsStore.getState()

  const timezone =
    properties && property && properties[property] ? properties[property]?.timezone : moment.tz.guess()

  const rainData =
    data?.points?.[String(precipitationStationLngLat)]?.values?.precipitation?.[0]?.timeseries ?? []

  const soilStationPoint =
    properties && property && soilStationLngLat
      ? properties[property]?.points?.[soilStationLngLat as TLngLat]
      : null

  const irrigationActivityScope = getIrrigationActivityScope({
    chosenScope: soilIrrigationActivityScope,
    soilPoint: soilStationPoint,
  })

  const soilMoistureMetadata = soilMoisture?.[0]?.metadata

  const emitterData =
    (irrigationActivityScope === 'PROBE'
      ? soilMoistureMetadata?.irrigationActivityForProbe
      : soilMoistureMetadata?.irrigationActivityForZones) ?? {}

  const rainSeries: TIrrigationEventSeries = makeRainSeries(rainData)
  const hasRain = rainSeries.data.length > 0

  const emitterRows = getEmitterDataForRows({
    availableEmitters: getAvailableEmittersForScope({
      irrigationActivityScope,
      soilPoint: soilStationPoint,
    }),
    emitterData,
    irrigationActivityScope,
  })

  const emitterSeries = makeEmitterSeries(emitterRows, hasRain)
  const hasWaterEvents = getHasWaterEvents({ hasRain, emitterData: emitterRows })

  // the legend lists every row of the scope, so in the zone view a device with only scheduled events has an entry too
  const availableEmittersForStation = emitterSeries.map(
    ({ irrigationZoneEmitterId }) => irrigationZoneEmitterId,
  )

  const chartItems: StackedChartSectionItem[] = []

  if (hasWaterEvents) {
    chartItems.push(
      makeWaterEventsChart({
        waterEventsSeries: hasRain ? [rainSeries, ...emitterSeries] : emitterSeries,
        rainSeries,
        firstForecastTimestamp: +new Date(),
        timezone,
        isDaily: aggregationInterval === 'DAILY',
      }),
    )
  }

  chartItems.push(
    makeAwcChart({
      aggregationInterval,
      availableEmittersForStation,
      hasRain,
      hasWaterEvents,
      chartId: commonReturnItems.id,
      compareSeasonsData,
      compareSeasonsInterval,
      data,
      firstForecastTimestamp,
      soilStationLngLat,
      soilStationFavoriteDepths,
    }),
  )

  return {
    ...commonReturnItems,
    items: chartItems,
    titleToggle: (
      <div css={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px' }}>
        <SoilAverageDepthsToggle />
        <SoilZoneDevicesToggle />
      </div>
    ),
  }
}
